import React, { Suspense, useContext } from 'react';
import lazyWithRetry from './lib/lazyWithRetry';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import ServiceWorkerUpdateBanner from './components/ServiceWorkerUpdateBanner';
import { AuthProvider, AuthContext } from './context/AuthContext';
import { PlatformAuthProvider } from './context/PlatformAuthContext';
import ProtectedRoute from './pages/ProtectedRoute';
import PlatformProtectedRoute from './pages/platform/PlatformProtectedRoute';
import LoginPage from './pages/LoginPage';
const RegisterPage = lazyWithRetry(() => import('./pages/RegisterPage'));
const VerifyEmailPage = lazyWithRetry(() => import('./pages/VerifyEmailPage'));
const ForgotPasswordPage = lazyWithRetry(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazyWithRetry(() => import('./pages/ResetPasswordPage'));
const LandingPage = lazyWithRetry(() => import('./pages/LandingPage'));
const GuestOrderPage = lazyWithRetry(() => import('./pages/public/GuestOrderPage'));
const GuestPortalPage = lazyWithRetry(() => import('./pages/public/GuestPortalPage'));
import Dashboard from './pages/dashboard/Dashboard';
const PlatformLoginPage = lazyWithRetry(() => import('./pages/platform/PlatformLoginPage'));
const PlatformShell = lazyWithRetry(() => import('./pages/platform/PlatformShell'));

// Routing structure:
//   /            -> public marketing landing page (LandingPage) for a
//                    signed-out visitor; an already-authenticated visitor is
//                    redirected straight to /app instead, since there's no
//                    reason to show marketing content to someone who's
//                    already signed in.
//   /login        -> public, staff + owner sign-in (LoginPage)
//   /register     -> public, self-serve hotel sign-up (RegisterPage)
//   /verify       -> public, reads ?token= and calls the verify endpoint
//   /forgot-password -> public, email -> POST /auth/forgot-password
//   /reset-password  -> public, reads ?token= + new password -> POST
//                    /auth/reset-password. Resets the OWNER account, not a
//                    per-hotel staff login — see worker/lib/
//                    passwordReset.js's module header for why.
//   /order        -> public, QR-code guest ordering (GuestOrderPage). Reads
//                    ?tenant=<slug>&table=<number>? — no login, no dashboard
//                    shell. See worker/lib/guestOrdering.js for the backend
//                    contract.
//   /app/*        -> protected (tenant session), redirects to /login
//   /platform/*   -> the TALIT superadmin console — a SEPARATE identity and
//                    token (see context/PlatformAuthContext.js); its own
//                    provider wraps only this subtree so it never touches
//                    the tenant AuthContext. /platform/login is public,
//                    everything else redirects there if not signed in.
function App() {
  return (
    <>
      <ServiceWorkerUpdateBanner />
      <BrowserRouter>
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-gray-600">Loading...</div>}>
      <Routes>
        {/* Platform console — its own auth provider, no tenant AuthProvider. */}
        <Route
          path="/platform/*"
          element={
            <PlatformAuthProvider>
              <Routes>
                <Route path="login" element={<PlatformLoginPage />} />
                <Route
                  path="*"
                  element={
                    <PlatformProtectedRoute>
                      <PlatformShell />
                    </PlatformProtectedRoute>
                  }
                />
              </Routes>
            </PlatformAuthProvider>
          }
        />

        {/* Tenant-facing app. */}
        <Route
          path="/*"
          element={
            <AuthProvider>
              <Routes>
                <Route path="/" element={<HomeRedirect />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/verify" element={<VerifyEmailPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/order" element={<GuestOrderPage />} />
                <Route path="/stay" element={<GuestPortalPage />} />
                <Route path="/stay/:token" element={<GuestPortalPage />} />
                <Route path="/s/:token" element={<GuestPortalPage />} />
                <Route
                  path="/app/*"
                  element={
                    <ProtectedRoute>
                      <DashboardRoute />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </AuthProvider>
          }
        />
      </Routes>
      </Suspense>
      </BrowserRouter>
    </>
  );
}

// Shows the public marketing landing page at "/" for a signed-out visitor.
// A signed-in visitor is redirected straight to /app instead — they've
// already converted, so the marketing page has nothing left to sell them.
function HomeRedirect() {
  const { user, loading } = useContext(AuthContext);
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  if (user) {
    return <Navigate to="/app" replace />;
  }
  return <LandingPage />;
}

// Wires the existing Dashboard component (which takes user/onLogout as
// props, not context) into the router without having to rewrite Dashboard
// itself for this phase.
function DashboardRoute() {
  const { user, logout } = useContext(AuthContext);
  return <Dashboard user={user} onLogout={logout} />;
}

export default App;
